import { Monitor01, Tag01, User01, Users01 } from "@untitled-ui/icons-react";
import { tagService } from "#src/services/tags";
import { endpointService } from "#src/services/endpoint";
import directoryService from "#src/services/endpoint/directory";
import {
  IPassportPort,
  IPassportDestination,
  IPassportSource,
} from "#src/services/endpoint/passports/types";
import {
  PassportRuleType,
  PassportProtocolType,
  ProtocolToDefaultPort,
  PassportSourceDestinationHostType,
  PassportSourceDestinationUserType,
} from "#src/services/endpoint/passports/enums";
import { Tag } from "#src/commonV2/TagsInputWithSearch";

// Convert passport ports to Tag format for the input component
export const passportPortsToTags = (passportPorts: IPassportPort[]): Tag[] => {
  return passportPorts.map((port) => ({
    id: `${port.protocol}-${port.port}`,
    name: port.protocol ? `${port.protocol} (${port.port})` : port.port,
    extraData: {
      protocol: port.protocol,
      port: port.port,
    },
  }));
};

// Convert Tag format back to passport ports
export const tagsToPassportPorts = (tags: Tag[]): IPassportPort[] => {
  return tags.map((tag) => {
    if (tag.extraData) {
      return {
        protocol: tag.extraData.protocol,
        port: tag.extraData.port,
      };
    }

    // Fallback in case extraData is missing
    return {
      protocol: PassportProtocolType.CUSTOM,
      port: tag.name,
    };
  });
};

// Search function for ports
export const searchPorts = async (query: string): Promise<Tag[]> => {
  const trimmedQuery = query.trim();
  const protocols = Object.entries(ProtocolToDefaultPort);
  const matchingProtocols = protocols.filter(([protocol, port]) => {
    const q = trimmedQuery.toLowerCase();
    return protocol.toLowerCase().includes(q) || port.includes(q);
  });

  // If we found matching protocols
  if (matchingProtocols.length > 0) {
    return matchingProtocols.map(([protocol, port]) => ({
      id: `${protocol}-${port}`,
      name: protocol ? `${protocol} (${port})` : port,
      extraData: {
        protocol: protocol as PassportProtocolType,
        port,
      },
      badgeOptions: {
        noColor: true,
      },
    }));
  }

  // Check if the query is numeric
  const isNumeric = /^\d+$/.test(trimmedQuery);

  // If query contains alphabets but doesn't match any protocol, return empty list
  if (!isNumeric && trimmedQuery.length > 0) {
    return [];
  }

  // If query is numeric, check if it exceeds the max port number (65535)
  if (isNumeric && parseInt(trimmedQuery, 10) > 65535) {
    return [];
  }

  // If query is a valid port number, treat it as a custom port
  if (isNumeric) {
    return [
      {
        id: `CUSTOM-${trimmedQuery}`,
        name: trimmedQuery,
        extraData: {
          protocol: PassportProtocolType.CUSTOM,
          port: trimmedQuery,
        },
        badgeOptions: {
          noColor: true,
        },
      },
    ];
  }

  // Return empty list for any other case
  return [];
};

// Convert passport destinations to Tag format for the input component
export const passportDestinationsToTags = (
  destinations: IPassportDestination[]
): Tag[] => {
  return destinations.map((destination) => ({
    id: `${destination.type}-${destination.id}`,
    name: destination.name,
    extraData: {
      id: destination.id,
      type: destination.type,
    },
    badgeOptions: {
      icon:
        destination.type === PassportSourceDestinationHostType.HOST ? (
          <Monitor01 className="h-4 w-4 min-w-4" />
        ) : undefined,
      defaultIcon: destination.type === PassportSourceDestinationHostType.TAG,
      noColor: destination.type === PassportSourceDestinationHostType.HOST,
    },
    dropdownOptions: {
      icon:
        destination.type === PassportSourceDestinationHostType.HOST ? (
          <Monitor01 className="h-4 w-4 min-w-4" />
        ) : (
          <Tag01 className="h-4 w-4 min-w-4" />
        ),
    },
  }));
};

// Convert Tag format back to passport destinations
export const tagsToPassportDestinations = (
  tags: Tag[]
): IPassportDestination[] => {
  return tags.map((tag) => {
    if (tag.extraData) {
      return {
        id: tag.extraData.id,
        name: tag.name,
        type: tag.extraData.type,
      };
    }
    // Fallback in case extraData is missing
    return {
      id: 0,
      name: tag.name,
      type: PassportSourceDestinationHostType.HOST,
    };
  });
};

// Search function for destinations
export const searchDestinations = async (
  workspaceId: string,
  query: string
): Promise<Tag[]> => {
  try {
    const [tags, devices] = await Promise.all([
      tagService.listTagsPaging(workspaceId, {
        q: query,
        page: 1,
        size: 10,
      }),
      endpointService.listDevices(workspaceId, {
        q: query,
        page: 1,
        size: 10,
        sort: undefined,
        os: undefined,
        host_status: undefined,
        tag: undefined,
        policy_compliance: undefined,
        policy_compliance_id: undefined,
        policy_compliance_status: undefined,
        program_id: undefined,
        asset_type: undefined,
        backup_enabled: undefined,
      }),
    ]);

    // Convert tags to our Tag format
    const tagResults = tags.results.map((tag) => ({
      id: `tag-${tag.id}`,
      name: tag.name,
      extraData: {
        id: tag.id,
        type: PassportSourceDestinationHostType.TAG,
      },
      badgeOptions: {
        defaultIcon: true,
        noColor: false,
      },
      dropdownOptions: {
        icon: <Tag01 className="h-5 w-5 min-w-5" />,
      },
    }));

    // Convert devices to our Tag format
    const deviceResults = devices.results.map((device) => ({
      id: `host-${device.id}`,
      name: device.name,
      extraData: {
        id: device.id,
        type: PassportSourceDestinationHostType.HOST,
      },
      badgeOptions: {
        icon: <Monitor01 className="h-4 w-4 min-w-4" />,
        noColor: true,
      },
      dropdownOptions: {
        icon: <Monitor01 className="h-5 w-5 min-w-5" />,
      },
    }));

    // Combine results
    return [...deviceResults, ...tagResults];
  } catch (error) {
    console.error("Error searching for destinations:", error);
    return [];
  }
};

// Convert passport sources to Tag format for the input component
export const passportSourcesToTags = (sources: IPassportSource[]): Tag[] => {
  return sources.map((source) => ({
    id: `${source.type}-${source.id}`,
    name: source.name,
    extraData: {
      id: source.id,
      type: source.type,
    },
    badgeOptions: {
      icon: (() => {
        if (source.type === PassportSourceDestinationHostType.HOST) {
          return <Monitor01 className="h-3 w-3 min-w-3" />;
        } else if (source.type === PassportSourceDestinationUserType.USER) {
          return <User01 className="h-3 w-3 min-w-3" />;
        } else if (
          source.type === PassportSourceDestinationUserType.DEPARTMENT
        ) {
          return <Users01 className="h-3 w-3 min-w-3" />;
        }
        return undefined;
      })(),
      defaultIcon: source.type === PassportSourceDestinationHostType.TAG,
      noColor: source.type !== PassportSourceDestinationHostType.TAG,
    },
    dropdownOptions: {
      icon: (() => {
        if (source.type === PassportSourceDestinationHostType.HOST) {
          return <Monitor01 className="h-5 w-5 min-w-5" />;
        } else if (source.type === PassportSourceDestinationUserType.USER) {
          return <User01 className="h-5 w-5 min-w-5" />;
        } else if (
          source.type === PassportSourceDestinationUserType.DEPARTMENT
        ) {
          return <Users01 className="h-5 w-5 min-w-5" />;
        }
        return undefined;
      })(),
    },
  }));
};

// Convert Tag format back to passport sources
export const tagsToPassportSources = (tags: Tag[]): IPassportSource[] => {
  return tags.map((tag) => {
    if (tag.extraData) {
      return {
        id: tag.extraData.id,
        name: tag.name,
        type: tag.extraData.type,
      };
    }
    // Fallback in case extraData is missing
    return {
      id: 0,
      name: tag.name,
      type: PassportSourceDestinationHostType.HOST,
    };
  });
};

// Search function for sources
export const searchSources = async (
  workspaceId: string,
  query: string,
  type: PassportRuleType
): Promise<Tag[]> => {
  try {
    if (type === PassportRuleType.HOST_HOST) {
      // Similar to destinations, search for hosts and tags
      const [tags, devices] = await Promise.all([
        tagService.listTagsPaging(workspaceId, {
          q: query,
          page: 1,
          size: 10,
        }),
        endpointService.listDevices(workspaceId, {
          q: query,
          page: 1,
          size: 10,
          sort: undefined,
          os: undefined,
          host_status: undefined,
          tag: undefined,
          policy_compliance: undefined,
          policy_compliance_id: undefined,
          policy_compliance_status: undefined,
          program_id: undefined,
          asset_type: undefined,
          backup_enabled: undefined,
        }),
      ]);

      // Convert tags to Tag format
      const tagResults = tags.results.map((tag) => ({
        id: `tag-${tag.id}`,
        name: tag.name,
        extraData: {
          id: tag.id,
          type: PassportSourceDestinationHostType.TAG,
        },
        badgeOptions: {
          defaultIcon: true,
          noColor: false,
        },
        dropdownOptions: {
          icon: <Tag01 className="h-5 w-5 min-w-5" />,
        },
      }));

      // Convert devices to Tag format
      const deviceResults = devices.results.map((device) => ({
        id: `host-${device.id}`,
        name: device.name,
        extraData: {
          id: device.id,
          type: PassportSourceDestinationHostType.HOST,
        },
        badgeOptions: {
          icon: <Monitor01 className="h-3 w-3 min-w-3" />,
          noColor: true,
        },
        dropdownOptions: {
          icon: <Monitor01 className="h-5 w-5 min-w-5" />,
        },
      }));

      // Combine results
      return [...deviceResults, ...tagResults];
    } else if (type === PassportRuleType.USER_HOST) {
      // For USER_HOST type, search for users and departments
      const [users, departments] = await Promise.all([
        directoryService.listUsers(workspaceId, {
          q: query,
          page: 1,
          size: 10,
          sort: undefined,
          vpn_enabled: undefined,
          department_id: undefined,
        }),
        directoryService.listAllDepartments(workspaceId, { q: query }),
      ]);

      // Convert users to Tag format
      const userResults = users.results.map((user) => ({
        id: `user-${user.id}`,
        name: user.full_name || user.email,
        extraData: {
          id: user.id,
          type: PassportSourceDestinationUserType.USER,
        },
        badgeOptions: {
          icon: <User01 className="h-3 w-3 min-w-3" />,
          noColor: true,
        },
        dropdownOptions: {
          icon: <User01 className="h-5 w-5 min-w-5" />,
        },
      }));

      // Convert departments to Tag format
      const departmentResults = departments.map((department) => ({
        id: `department-${department.id}`,
        name: department.name,
        extraData: {
          id: department.id,
          type: PassportSourceDestinationUserType.DEPARTMENT,
        },
        badgeOptions: {
          icon: <Users01 className="h-3 w-3 min-w-3" />,
          noColor: true,
        },
        dropdownOptions: {
          icon: <User01 className="h-5 w-5 min-w-5" />,
        },
      }));

      // Combine results
      return [...userResults, ...departmentResults];
    }

    return [];
  } catch (error) {
    console.error("Error searching for sources:", error);
    return [];
  }
};
